import React, { useState, useEffect, useMemo } from "react";
import { 
  Modal, 
  Radio, 
  DatePicker, 
  Select, 
  Button, 
  Space, 
  Typography, 
  Alert,
  Divider 
} from "antd";
import { getCompanySalesReps } from "../../../services/userService";
import useBodyScrollLock from "../../../hooks/useBodyScrollLock";
import "./ExportModal.scss";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

/**
 * ExportModal Component
 * --------------------
 * Modern modal for exporting merchant data to CSV with filtering options.
 * Built with Ant Design for a beautiful, consistent UI.
 * 
 * Props:
 * - isOpen: boolean to control modal visibility
 * - onClose: callback to close modal
 * - onExport: callback to handle export with filters
 * - company: current company context
 * - availableStatuses: array of available status options
 */
const ExportModal = ({ 
  isOpen, 
  onClose, 
  onExport, 
  company,
  availableStatuses = []
}) => {
  // Export type state
  const [exportType, setExportType] = useState('all');
  
  // Filter states
  const [filters, setFilters] = useState({
    dateRange: null,
    status: [],
    salesRep: undefined,
    accountExecutive: undefined
  });

  // UI states
  const [isExporting, setIsExporting] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(false);
  
  // Custom status input state
  const [customStatusInput, setCustomStatusInput] = useState("");
  
  // Agents state for sales rep filtering
  const [agents, setAgents] = useState([]);
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);
  const [hasLoadedAgents, setHasLoadedAgents] = useState(false);

  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  // Lazy loader for agents when dropdown is opened/focused
  const loadAgentsIfNeeded = async () => {
    if (!company || isAgentsLoading || hasLoadedAgents) return;
    try {
      setIsAgentsLoading(true);
      const agentNames = await getCompanySalesReps(company);
      setAgents(agentNames || []);
      setHasLoadedAgents(true);
    } catch (error) {
      console.error("Error fetching sales agents:", error);
      setAgents([]);
    } finally {
      setIsAgentsLoading(false);
    }
  };

  // Reset modal state when opened/closed
  useEffect(() => {
    if (isOpen) {
      setExportType('all');
      setFilters({
        dateRange: null,
        status: [],
        salesRep: undefined,
        accountExecutive: undefined
      });
      setDateRangeError(false);
      setCustomStatusInput("");
      setAgents([]);
      setHasLoadedAgents(false);
    }
  }, [isOpen]);
  
  // Reset agents cache when company changes while modal is open
  useEffect(() => {
    if (isOpen) {
      setAgents([]);
      setHasLoadedAgents(false);
    }
  }, [company, isOpen]);
  
  // Agents are already filtered by company from API - just use them directly
  const availableSalesReps = useMemo(() => {
    return agents || [];
  }, [agents]);

  // Event handlers
  const handleExportTypeChange = (e) => {
    setExportType(e.target.value);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const diffDays = end.diff(start, 'day');
      
      if (diffDays > 31) {
        setDateRangeError(true);
      } else {
        setDateRangeError(false);
        setFilters(prev => ({
          ...prev,
          dateRange: dates
        }));
      }
    } else {
      setDateRangeError(false);
      setFilters(prev => ({
        ...prev,
        dateRange: null
      }));
    }
  };

  const handleStatusChange = (selectedStatuses) => {
    setFilters(prev => ({
      ...prev,
      status: selectedStatuses
    }));
  };

  const handleSalesRepChange = (value) => {
    if (company === 'CyberSource') {
      setFilters(prev => ({
        ...prev,
        accountExecutive: value
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        salesRep: value
      }));
    }
  };

  // Handle adding custom status
  const handleAddCustomStatus = () => {
    const trimmedStatus = customStatusInput.trim();
    if (trimmedStatus && !filters.status.includes(trimmedStatus)) {
      setFilters(prev => ({
        ...prev,
        status: [...prev.status, trimmedStatus]
      }));
      setCustomStatusInput("");
    }
  };

  // Handle Enter key press in custom status input
  const handleCustomStatusKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddCustomStatus();
    }
  };

  const handleExport = async () => {
    if (dateRangeError) {
      return;
    }

    setIsExporting(true);
    try {
      // Convert date range to the format expected by the parent
      const exportFilters = {
        startDate: filters.dateRange ? filters.dateRange[0].format('YYYY-MM-DD') : '',
        endDate: filters.dateRange ? filters.dateRange[1].format('YYYY-MM-DD') : '',
        status: filters.status,
        salesRep: company === 'CyberSource' ? (filters.accountExecutive || '') : (filters.salesRep || ''),
        accountExecutive: company === 'CyberSource' ? (filters.accountExecutive || '') : ''
      };
      
      await onExport(exportType, exportFilters);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Modal
      title="Export to CSV"
      open={isOpen}
      onCancel={handleClose}
      footer={[
        <Button 
          key="cancel" 
          onClick={handleClose}
          disabled={isExporting}
        >
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          onClick={handleExport}
          loading={isExporting}
          disabled={dateRangeError}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Export CSV
        </Button>
      ]}
      width={700}
      centered
      className="export-modal-antd"
      destroyOnClose={true}
      transitionName=""
      maskTransitionName=""
      getContainer={false}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {/* Export Type Selection */}
        <div>
          <Title level={5} style={{ marginBottom: 8, marginTop: 0 }}>
            Export Options
          </Title>
          <Radio.Group 
            onChange={handleExportTypeChange} 
            value={exportType}
            disabled={isExporting}
            style={{ width: '100%' }}
            className="export-radio-group"
          >
            <div className="export-options-grid">
              <Radio value="all" className="export-radio-option">
                <span style={{ fontWeight: 500, fontSize: 14 }}>
                  Export all data currently showing on the page
                </span>
              </Radio>
              <Radio value="filtered" className="export-radio-option">
                <span style={{ fontWeight: 500, fontSize: 14 }}>
                  Select filters to export specific data
                </span>
              </Radio>
            </div>
          </Radio.Group>
        </div>

        {/* Filter Options - Only show when filtered export is selected */}
        {exportType === 'filtered' && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <div>
              <Title level={5} style={{ marginBottom: 8, marginTop: 0 }}>
                Filter Options
              </Title>
              
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {/* Date Range Filter */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
                    Date Range (Maximum 31 days)
                  </Text>
                  <RangePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    onChange={handleDateRangeChange}
                    disabled={isExporting}
                    placeholder={['Start Date', 'End Date']}
                  />
                  {dateRangeError && (
                    <Alert
                      message="Date range cannot exceed 31 days"
                      type="error"
                      showIcon
                      style={{ marginTop: 4 }}
                      banner
                    />
                  )}
                </div>

                {/* Status and Sales Rep Filters */}
                <Space size={8} style={{ width: '100%', display: 'flex' }}>
                  {/* Status Filter */}
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
                      Status (Multiple)
                    </Text>
                    
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder="Select Status"
                      value={filters.status}
                      onChange={handleStatusChange}
                      disabled={isExporting}
                      maxTagCount="responsive"
                      virtual={true}
                      listHeight={200}
                      placement="topLeft"
                      popupMatchSelectWidth={false}
                      options={availableStatuses.map(status => ({
                        label: status,
                        value: status
                      }))}
                      dropdownRender={(menu) => (
                        <>
                          {/* Custom Status Input inside dropdown */}
                          <div className="custom-status-input-wrapper-export">
                            <input
                              type="text"
                              className="custom-status-input-export"
                              placeholder="Type status..."
                              value={customStatusInput}
                              onChange={(e) => setCustomStatusInput(e.target.value)}
                              onKeyPress={handleCustomStatusKeyPress}
                              disabled={isExporting}
                              onMouseDown={(e) => e.stopPropagation()}
                            />
                            <button
                              type="button"
                              className="add-status-btn-export"
                              onClick={handleAddCustomStatus}
                              disabled={!customStatusInput.trim() || isExporting}
                              title="Add status"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                          </div>
                          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: '4px', paddingTop: '4px' }}>
                            {menu}
                          </div>
                        </>
                      )}
                    />
                  </div>

                  {/* Sales Rep / Account Executive Filter */}
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
                      {company === 'CyberSource' ? 'Account Executive' : 'Sales Rep'}
                    </Text>
                    <Select
                      style={{ width: '100%' }}
                      placeholder={isAgentsLoading ? 'Loading agents…' : (company === 'CyberSource' ? 'Account Executive' : 'Sales Rep')}
                      value={company === 'CyberSource' ? filters.accountExecutive : filters.salesRep}
                      onChange={handleSalesRepChange}
                      disabled={isExporting}
                      allowClear={!isAgentsLoading}
                      virtual={true}
                      placement="topLeft"
                      popupMatchSelectWidth={false}
                      loading={isAgentsLoading}
                      suffixIcon={isAgentsLoading ? <span className="spinner-border spinner-border-sm text-success" /> : undefined}
                      notFoundContent={
                        isAgentsLoading ? (
                          <div className="d-flex justify-content-center align-items-center py-2" style={{ color: '#198754' }}>
                            <span className="spinner-border spinner-border-sm text-success me-2" role="status" />
                            Loading agents…
                          </div>
                        ) : (hasLoadedAgents ? null : 'Open to load agents')
                      }
                    onDropdownVisibleChange={(open) => {
                      if (open) loadAgentsIfNeeded();
                    }}
                    onFocus={loadAgentsIfNeeded}
                      options={[
                        { 
                          label: company === 'CyberSource' ? 'All Account Executives' : 'All Sales Reps', 
                          value: '' 
                        },
                        ...availableSalesReps.map(agent => ({
                          label: agent.name,
                          value: agent.name
                        }))
                      ]}
                    />
                  </div>
                </Space>
              </Space>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default React.memo(ExportModal);
